import {
  Component,
  Inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ToolbarDrawerService } from '../toolbar-drawer.service';
import { PlaybackState } from '../playback-controller';
import { ToolbarControlContext, TOOLBAR_CONTROL_CONTEXT } from '../toolbar-control.tokens';
import { BaseControl, formatUtcShort, formatUtcStamp } from './base-control';

/**
 * Transport + display control plugins for the pluggable toolbar host.
 * Each is a dumb button bound to the shared `PlaybackController.state$`.
 * Visual styling is provided by `toolbar-host.component.scss` (::ng-deep .tb-*).
 *
 * Every one of these is icon-only and carries a `matTooltip` rather than a
 * label: the bar floats on the chart, and the Voyage Radar sidebar next to it is
 * already narrating in words what is drawn. `matTooltip` rather than `title=`
 * so the hint is themed like the rest of the panel and appears immediately
 * instead of after the browser's own delay.
 */

export { BaseControl };

@Component({
  selector: 'app-close-control',
  template: `
    <button type="button" class="tb-btn" (click)="ctrl.resumeLive()" aria-label="Close playback"
            matTooltip="Exit playback" matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">close</mat-icon>
    </button>`,
})
export class CloseControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

@Component({
  selector: 'app-play-pause-control',
  template: `
    <button type="button" class="tb-btn tb-btn--primary" (click)="ctrl.togglePlay()"
            [attr.aria-label]="(state$ | async)?.isPlaying ? 'Pause' : 'Play'"
            [matTooltip]="(state$ | async)?.isPlaying ? 'Pause' : 'Play'"
            matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">{{ (state$ | async)?.isPlaying ? 'pause' : 'play_arrow' }}</mat-icon>
    </button>`,
})
export class PlayPauseControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

@Component({
  selector: 'app-skip-start-control',
  template: `
    <button type="button" class="tb-btn" (click)="ctrl.skipToStart()" aria-label="Skip to start"
            matTooltip="Skip to start" matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">first_page</mat-icon>
    </button>`,
})
export class SkipStartControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

@Component({
  selector: 'app-step-back-control',
  template: `
    <button type="button" class="tb-btn" (click)="ctrl.stepBack()" aria-label="Step back"
            matTooltip="Step back" matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">fast_rewind</mat-icon>
    </button>`,
})
export class StepBackControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

/**
 * Deliberately NOT `--primary`: there is exactly one accent circle in the bar
 * and it is play/pause. A second one next to it turns the transport into two
 * competing buttons with no way to tell which one starts playback.
 */
@Component({
  selector: 'app-step-forward-control',
  template: `
    <button type="button" class="tb-btn" (click)="ctrl.stepForward()" aria-label="Step forward"
            matTooltip="Step forward" matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">fast_forward</mat-icon>
    </button>`,
})
export class StepForwardControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

@Component({
  selector: 'app-skip-end-control',
  template: `
    <button type="button" class="tb-btn" (click)="ctrl.skipToEnd()" aria-label="Skip to end"
            matTooltip="Skip to end" matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">last_page</mat-icon>
    </button>`,
})
export class SkipEndControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

/**
 * The scrub rail.
 *
 * A real `<input type="range">` does the behaviour — pointer, keyboard, screen
 * readers — and is then made invisible, with the fill and thumb drawn as spans
 * behind it. That indirection is what lets the fill take the traced vessel's own
 * colour: a native thumb is reachable only through pseudo-elements, and Angular
 * 14 cannot bind a custom property to feed them.
 *
 * The timestamp is a bubble that appears on hover, drag or keyboard focus rather
 * than a permanent label. This bar is icon-only at rest by design, and the
 * sidebar's ON MAP bar is showing the same clock in words the whole time.
 */
@Component({
  selector: 'app-timeline-control',
  template: `
    <div class="tb-rail" *ngIf="state$ | async as s">
      <span class="tb-rail__fill" [style.width.%]="pct(s)"
            [style.background]="s.activeVesselColor || null"></span>
      <span class="tb-rail__thumb" [style.left.%]="pct(s)"
            [style.background]="s.activeVesselColor || null"></span>
      <span class="tb-rail__stamp" [style.left.%]="pct(s)">{{ shortLabel(s) }}</span>
      <input type="range" class="tb-rail__input" min="0" [max]="max(s)" step="1"
             [value]="s.currentIndex"
             aria-label="Timeline position"
             [attr.aria-valuetext]="label(s)"
             (input)="ctrl.seek(+$any($event.target).value)">
    </div>`,
})
export class TimelineControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }

  max(s: PlaybackState): number {
    return Math.max(0, s.stepCount - 1);
  }

  /** 0–100 across the loaded steps — drives both the fill and the thumb. */
  pct(s: PlaybackState): number {
    const last = this.max(s);
    if (last <= 0) return 0;
    return Math.min(100, Math.max(0, (s.currentIndex / last) * 100));
  }

  // The bubble is centred on the thumb by `translateX(-50%)` in the stylesheet
  // and takes `pct` unmodified, so it sits over the position it describes at
  // every point on the rail — including the two ends.
  //
  // It used to be clamped to 12%–88% of the rail, which is what put it "off
  // position": the clamp stops the bubble while the thumb keeps going, so at
  // either extreme the label parked ~24px away from the dot it was pointing at.
  // The clamp was guarding against the bubble leaving the toolbar, but it was
  // measured in the RAIL's percent while the overflow it feared is in the
  // BUBBLE's pixels, so it never actually prevented that either.
  //
  // Nothing replaces it, because the rail is the wrong thing to be contained by.
  // The rail is 200px inside a bar several hundred wide, and the bubble floats
  // ABOVE that bar rather than inside it, so half a ~105px label overhanging the
  // rail's ends lands over the transport buttons' airspace and is free. Only a
  // toolbar narrower than the rail plus a label could push it off screen, and
  // this control only ever renders in the full transport.

  /** Full stamp — announced to screen readers, never painted. */
  label(s: PlaybackState): string {
    const iso = s.stepLabels[s.currentIndex];
    return iso ? formatUtcStamp(iso) : '';
  }

  /** "21 Jul 20:02 UTC" — what the bubble shows. */
  shortLabel(s: PlaybackState): string {
    const iso = s.stepLabels[s.currentIndex];
    return iso ? formatUtcShort(iso) : '';
  }
}

@Component({
  selector: 'app-speed-control',
  template: `
    <ng-container *ngIf="state$ | async as s">
      <button #trigger type="button" class="tb-speed-trigger"
              [class.tb-btn--active]="drawer.isOpen(panel)"
              (click)="drawer.toggle(panel, vcr, trigger)"
              aria-label="Playback speed"
              matTooltip="Playback speed" matTooltipPosition="above">
        <span class="tb-speed-val">{{ s.speed }}&times;</span>
        <mat-icon class="tb-speed-caret">arrow_drop_down</mat-icon>
      </button>
    </ng-container>

    <!-- Declared outside the *ngIf: the open overlay holds this TemplateRef. -->
    <ng-template #panel>
      <app-toolbar-drawer *ngIf="state$ | async as s"
                          title="Speed" (close)="drawer.close()">
        <div class="tb-drawer__chips">
          <button type="button" class="tb-chip" *ngFor="let opt of speeds"
                  [class.tb-chip--on]="opt === s.speed"
                  (click)="pick(opt)">{{ opt }}&times;</button>
        </div>
      </app-toolbar-drawer>
    </ng-template>`,
  // Chip styling comes from the drawer's shared vocabulary (::ng-deep) — this
  // content is projected, so a stylesheet here would not reach it.
})
export class SpeedControlComponent extends BaseControl implements OnDestroy {
  @ViewChild('panel') panel!: TemplateRef<any>;

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
  ) {
    super();
  }

  get speeds(): number[] {
    return this.context.options?.['speeds'] ?? [1000, 600, 300, 200, 100, 60, 30, 10, 5, 2, 1];
  }

  pick(speed: number): void {
    this.ctrl.setSpeed(speed);
    this.drawer.close();
  }

  ngOnDestroy(): void {
    this.drawer.closeIfOpen(this.panel);
  }
}

/**
 * Was a serif "T" glyph — the one thing in the old toolbar that was neither an
 * icon nor a word. An outlined icon puts it in the same family as every other
 * button, and the active state says whether labels are on.
 */
@Component({
  selector: 'app-labels-toggle-control',
  template: `
    <button type="button" class="tb-btn" [class.tb-btn--active]="(state$ | async)?.showLabels"
            (click)="ctrl.toggleLabels()" aria-label="Toggle vessel labels"
            [matTooltip]="(state$ | async)?.showLabels ? 'Hide vessel labels' : 'Show vessel labels'"
            matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">text_fields</mat-icon>
    </button>`,
})
export class LabelsToggleControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

/**
 * Group nearby vessels into count bubbles, or draw every hull.
 *
 * Lit while grouping is ON, which is also the default — the same grammar as the
 * labels button beside it, where the active state says what the map is currently
 * doing rather than what the click will do.
 *
 * `bubble_chart` — circles of different sizes, which is literally what the
 * control produces. Not `workspaces` or `grain`: this bar already carries
 * `blur_on` for the track's fleet dim, and a second abstract dot-cloud glyph
 * beside it would be two buttons that both mean "the other vessels, somehow".
 */
@Component({
  selector: 'app-cluster-toggle-control',
  template: `
    <button type="button" class="tb-btn" [class.tb-btn--active]="(state$ | async)?.clusterVessels"
            (click)="ctrl.toggleClusters()" aria-label="Toggle vessel grouping"
            [matTooltip]="(state$ | async)?.clusterVessels
              ? 'Show every vessel'
              : 'Group nearby vessels'"
            matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">bubble_chart</mat-icon>
    </button>`,
})
export class ClusterToggleControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}
